import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import FlashSnackbar from '../common/FlashSnackbar';
import { DS, tealButtonSx } from './designTokens';
import { useJournalCopy } from './useJournalCopy';

interface JournalCopyPanelProps {
  children: React.ReactNode;
}

const JournalCopyPanel: React.FC<JournalCopyPanelProps> = ({ children }) => {
  const { t } = useTranslation();
  const { flash, open, handleCopy, clearFlash } = useJournalCopy();
  const [highlight, setHighlight] = useState(false);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: highlight ? DS.deepTeal : DS.hairline,
        backgroundColor: highlight ? DS.coolMint : DS.surfaceCanvas,
        borderRadius: DS.radiusLg,
        p: 1.5,
        transition: 'border-color 150ms, background-color 150ms',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 1.5,
          pl: 0.5,
        }}
      >
        <Typography
          id="journal-copy-hint"
          sx={{
            fontFamily: DS.fontBody,
            fontSize: '13px',
            color: DS.inkSecondary,
          }}
        >
          {t('journalCopy.reviewHint')}
        </Typography>
        <Button
          startIcon={<ContentCopyIcon sx={{ fontSize: '16px !important' }} />}
          onClick={handleCopy}
          onMouseEnter={() => setHighlight(true)}
          onMouseLeave={() => setHighlight(false)}
          onFocus={() => setHighlight(true)}
          onBlur={() => setHighlight(false)}
          size="small"
          aria-describedby="journal-copy-hint"
          sx={{
            ...tealButtonSx,
            fontSize: '13px',
            px: 2,
            py: 0.5,
            flexShrink: 0,
          }}
        >
          {t('journalCopy.button')}
        </Button>
      </Box>

      <Box
        id="journal-copy-content"
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        {children}
      </Box>

      <FlashSnackbar
        open={open}
        msg={flash ? t(flash.msgKey) : ''}
        severity={flash?.severity}
        onClose={clearFlash}
      />
    </Box>
  );
};

export default JournalCopyPanel;
